import { APP } from '_pixiSrc/globals';
import Sprite from '_pixiSrc/display/Sprite';
import { Utils } from '_pixiSrc/Utils';
import AudioManager from './AudioManager';
import GameData from './GameData';

/**
 * @class SliderButton
 * @extends {Sprite}
 * @classdesc Used to give slider behaviour to button.
 */
class SliderButton extends Sprite {
    /**
     * Creates an instance of SliderButton.
     * @param {number} [x=0]
     * @param {number} [y=0]
     * @param {number} [scale=1]
     * @param {string} keyToBind  Game property to bind for silder ON/OFF.
     * @param {Function} [clickAction=() => {}  handle state change call.
     */
    constructor(x = 0, y = 0, scale = 1, keyToBind, clickAction = () => { /* empty */ }) {
        super();

        this.x = x;
        this.y = y;
        this.isOn = GameData.getProperty(keyToBind);

        this.scale.set(scale);

        this.keyToBind = keyToBind;
        this.clickAction = clickAction;

        this.isDisabled = false;

        this.interactive = true;
        this.buttonMode = true;
        this.cursor = 'pointer';

        this.once('added', Utils.proxy(this._init, this));

        // position on OFF state
        this.startPosition = -19;
        // position on ON state
        this.endPosition = 19;

        // use to set starting X position on drag start
        this.startX = 0;
    }


    /**
     * Initialize slider behaviour for sprite.
     */
    _init() {
        this.inactiveBackground = this.addChild(APP.library.getSprite('mobile-ui/slider-inactive'));

        this.activeBackground = this.addChild(APP.library.getSprite('mobile-ui/slider-active'));

        this.slider = this.addChild(APP.library.getSprite('mobile-ui/slider'));

        this.updateState();

        this.hitArea = new PIXI.Ellipse(0, 0, 70, 20);

        this.changeSliderPosition(0);

        // handler pointer down event to mark start position before dragging
        this.on('pointerdown', (e) => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = true;
            this.startX = e.data.global.x;

            // handle pointermove event on gamescreen sprite
            APP.currentWindow.on('pointermove', this.handlePointerMove, this);
        });

        this.on('pointerup', (e) => {
            // remove pointermove event from gamescreen sprite
            APP.currentWindow.off('pointermove', this.handlePointerMove, this);
            if (this.isDisabled) {
                return;
            }
            /**
             * if on pointer up user not slide the button but just tap
             * then change state to opposite of current state.
             */
            if (this.isPointerDown && Math.abs(this.startX - e.data.global.x) <= 10) {
                this.isOn = !this.isOn;
                this.onClickHandler();
            }
            this.isPointerDown = false;
        });

        this.on('pointerupoutside', () => {
            // remove pointermove event from gamescreen sprite
            APP.currentWindow.off('pointermove', this.handlePointerMove, this);
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = false;
        });
    }

    /**
     * handle pointer move event
     * for finding draggin direction and set button state as direction
     * right direction its ON and left direction its OFF
     * @param {*} e
     */
    handlePointerMove(e) {
        if (this.isDisabled || !this.isPointerDown) {
            return;
        }
        if (Math.abs(this.startX - e.data.global.x) > 10) {
            if (this.isOn !== e.data.global.x > this.startX) {
                this.isOn = !this.isOn;
                this.onClickHandler();
            }
        }
    }

    /**
     * click handler for changing slider position.
     */
    onClickHandler() {
        if (this.isDisabled) {
            return;
        }
        AudioManager.playSound('fx', 'btns');
        this.isDisabled = true;
        this.updateState();
        this.changeSliderPosition();
        GameData.setProperty(this.keyToBind, this.isOn);
        this.clickAction(this.isOn);
    }

    /**
     * manage slider background as its current ON/OFF state
     */
    updateState() {
        this.activeBackground.visible = this.isOn;
        this.inactiveBackground.visible = !this.isOn;
    }

    /**
     * manage slider position as its current ON/OFF state
     */
    changeSliderPosition(duration = 150) {
        if (this.isOn) {
            this.slider.moveTo(this.endPosition, 0, duration, null, () => {
                this.isDisabled = false;
            });
            return;
        }
        this.slider.moveTo(this.startPosition, 0, duration, null, () => {
            this.isDisabled = false;
        });
    }
}

export default SliderButton;
